import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { format } from "date-fns";
import { PostData } from "../../services/FetchData";
import { parseAssistantMarkdown } from "./conversationDisplay";

function reasonBadge(reason) {
	const r = String(reason || "").toLowerCase();
	if (r === "outside_hours") return <Badge color="warning">Outside hours</Badge>;
	if (r === "no_agents_online") return <Badge color="secondary">No agent online</Badge>;
	return <Badge color="light">Offline</Badge>;
}

const OfflineLeadsTab = ({ websiteId, active }) => {
	const [loader, setLoader] = useState(true);
	const [leads, setLeads] = useState([]);
	const [selected, setSelected] = useState(null);

	const load = useCallback(async () => {
		if (!websiteId || !active) return;
		setLoader(true);
		try {
			const res = await PostData("ms1", "api/v1/aiassistant/shopping-assistant-sessions", {
				websiteId,
				scope: "offlineLeads",
			});
			const list = res?.status === "success" && Array.isArray(res?.data?.sessions) ? res.data.sessions : [];
			setLeads(list);
			setSelected((prev) => {
				if (!list.length) return null;
				if (prev && list.some((l) => l.leadId === prev.leadId)) return prev;
				return list[0];
			});
		} catch (e) {
			console.error(e);
			setLeads([]);
		} finally {
			setLoader(false);
		}
	}, [websiteId, active]);

	useEffect(() => {
		load();
	}, [load]);

	if (!active) return null;

	return (
		<div className="chat-container d-flex" style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
			<div
				style={{
					width: 320,
					minWidth: 320,
					borderRight: "1px solid #dee2e6",
					backgroundColor: "#f8f9fa",
					overflowY: "auto",
				}}
			>
				{loader ? (
					<div className="p-3">
						<Skeleton count={5} height={48} />
					</div>
				) : leads.length === 0 ? (
					<p className="p-3 text-muted small mb-0">No offline requests yet.</p>
				) : (
					leads.map((lead) => (
						<button
							key={lead.leadId}
							type="button"
							className={`w-100 text-start border-0 border-bottom px-3 py-2 ${
								selected?.leadId === lead.leadId ? "bg-white" : "bg-transparent"
							}`}
							onClick={() => setSelected(lead)}
						>
							<div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>
								{lead.customerName || lead.email || "Visitor"}
							</div>
							<div className="text-muted small text-truncate">{lead.customerEmail}</div>
							<div className="d-flex align-items-center gap-2 mt-1">
								{reasonBadge(lead.unavailableReason)}
								{lead.dateTime ? (
									<span className="text-muted" style={{ fontSize: 11 }}>
										{format(new Date(lead.dateTime), "MM/dd/yy h:mm a")}
									</span>
								) : null}
							</div>
						</button>
					))
				)}
			</div>
			<div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
				{!selected ? (
					<div className="p-4 text-muted">Select an offline request.</div>
				) : (
					<>
						<div className="p-3 border-bottom bg-white">
							<h6 className="mb-1">{selected.customerName}</h6>
							<div className="small text-muted">{selected.customerEmail}</div>
							{selected.phone ? <div className="small">Phone: {selected.phone}</div> : null}
							{selected.url ? (
								<div className="small mt-1">
									<a href={selected.url} target="_blank" rel="noreferrer">
										{selected.url}
									</a>
								</div>
							) : null}
							{selected.agentSessionId ? (
								<div className="small text-muted mt-1">Bot session: {selected.agentSessionId}</div>
							) : null}
						</div>
						<div className="p-3 flex-grow-1 overflow-auto" style={{ backgroundColor: "#f8f9fa" }}>
							{selected.message ? (
								<div className="mb-3 p-2 bg-white rounded border">
									<div className="small text-muted mb-1">Message</div>
									<div style={{ whiteSpace: "pre-wrap" }}>{selected.message}</div>
								</div>
							) : null}
							{Array.isArray(selected.conversation) && selected.conversation.length > 0 ? (
								<>
									<div className="small text-muted mb-2">Conversation snapshot</div>
									{selected.conversation.map((turn, idx) => (
										<div
											key={idx}
											className="mb-2 p-2 rounded"
											style={{
												backgroundColor: turn.role === "user" ? "#e9ecef" : "#fff",
												border: "1px solid #dee2e6",
											}}
										>
											<div className="small fw-semibold mb-1">
												{turn.role === "user" ? "Customer" : "Assistant"}
											</div>
											<div
												className="cb-sa-conv-msg-text"
												style={{ fontSize: 13 }}
												dangerouslySetInnerHTML={{
													__html: parseAssistantMarkdown(turn.content || ""),
												}}
											/>
										</div>
									))}
								</>
							) : null}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default OfflineLeadsTab;
